import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, input, model } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxOptionsGroup } from '@onyx/angular';
import {
  OnyxButtonComponent,
  OnyxDropdownDirective,
  OnyxIconComponent,
  OnyxMessageComponent,
  OnyxModalService,
  OnyxToastService,
} from '@onyx/angular';
import type { Observable, Subject } from 'rxjs';
import { map } from 'rxjs';
import { DictionaryCode } from '../../../../../../../common/enums/dictionary-code';
import { DictionariesService } from '../../../../../../../common/services/dictionaries.service';
import { OrderPlanningMode } from '../../../../../common/enums/order-planning-mode';
import { OrderRejectReason } from '../../../../../common/enums/order-reject-reason';
import { OrderHelper } from '../../../../../common/helpers/order.helper';
import type { Order } from '../../../../../common/interfaces/order';
import { OrdersService } from '../../../../../common/services/orders.service';
import type { OtherReasonModalData } from './other-reason-modal/other-reason-modal.component';
import {
  OtherReasonModalComponent,
  OtherReasonModalMode,
} from './other-reason-modal/other-reason-modal.component';

@Component({
  selector: 'app-order-modal-decision-buttons',
  imports: [
    OnyxButtonComponent,
    TranslatePipe,
    OnyxDropdownDirective,
    AsyncPipe,
    OnyxMessageComponent,
    OnyxIconComponent,
  ],
  templateUrl: './order-modal-decision-buttons.component.html',
  styleUrl: './order-modal-decision-buttons.component.scss',
})
export class OrderModalDecisionButtonsComponent {
  private readonly toastService = inject(OnyxToastService);
  private readonly modalService = inject(OnyxModalService);
  private readonly ordersService = inject(OrdersService);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly orderHelper = inject(OrderHelper);

  public readonly tab = model<OrderPlanningMode>();
  public readonly order = input.required<Order>();
  public readonly close$ = input.required<Subject<void>>();

  protected readonly I18N = 'orders.orderModal';

  protected readonly rejectReasons$ = this.dictionariesService.getDictionary(
    DictionaryCode.ORDER_REJECT_REASON,
  );
  protected readonly rejectOptions$ = this.getOptions(
    OtherReasonModalMode.REJECT,
  );
  protected readonly changeOptions$ = this.getOptions(
    OtherReasonModalMode.CHANGE,
  );

  protected engineProposal = computed(() =>
    OrderHelper.getEngineProposal(this.order()),
  );

  protected showOnMap(): void {
    this.orderHelper.showOnMap(this.order(), { close$: this.close$() });
  }

  protected accept(): void {
    const order = this.order();

    this.ordersService
      .acceptVehicle(order.uuid, this.engineProposal()!.vehicle.uuid)
      .subscribe(() =>
        this.toastService.showSuccess({
          message: `${this.I18N}.toasts.vehicleAccepted`,
        }),
      );
  }

  private change(reason: OrderRejectReason): void {
    this.handleReason(reason, OtherReasonModalMode.CHANGE);
  }

  private reject(reason: OrderRejectReason): void {
    this.handleReason(reason, OtherReasonModalMode.REJECT);
  }

  private handleReason(
    reason: OrderRejectReason,
    mode: OtherReasonModalMode,
  ): void {
    const order = this.order();
    const vehicleUuid = this.engineProposal()!.vehicle.uuid;

    if (reason === OrderRejectReason.OTHER) {
      this.modalService
        .open<OtherReasonModalData>(OtherReasonModalComponent, {
          mode,
          orderUuid: order.uuid,
          vehicleUuid,
        })
        .result.subscribe((result) => {
          if (result && mode === OtherReasonModalMode.CHANGE) {
            this.tab.set(OrderPlanningMode.MANUAL);
          }
        });
      return;
    }

    this.ordersService
      .rejectVehicle(order.uuid, {
        vehicleUuid,
        reason,
        other: null,
      })
      .subscribe(() => {
        if (mode === OtherReasonModalMode.CHANGE) {
          this.tab.set(OrderPlanningMode.MANUAL);
        } else {
          this.toastService.showInformation({
            message: `${this.I18N}.toasts.vehicleRejected`,
          });
        }
      });
  }

  private getOptions(
    action: OtherReasonModalMode,
  ): Observable<OnyxOptionsGroup<() => void>[]> {
    return this.rejectReasons$.pipe(
      map((reasons, index) => [
        {
          id: `${index}.default`,
          subheading: `${this.I18N}.rejectDropdown.subheading`,
          options: reasons.map((reason) => ({
            name: reason.name,
            value: () => this[action](reason.value),
          })),
        },
      ]),
    );
  }
}
